import { HqApiService } from 'hq/services/hqApiService';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import { ShiftBulkArchivePayload } from 'hq/services/shift-planning-service.types';
import { BreakOption } from '@types';
import {
  BulkShiftActionData,
  bulkShiftActionDataSchema,
  getBreakOptionsSchema,
} from './schema/bulk-shift-action-data.schema';
import {
  BulkShiftActionDataResponseDto,
  CreateMultiDayShiftRequestDto,
  CreateShiftRequestDto,
  DuplicateShiftRequestDto,
  GetShiftCreateDataResponseDto,
  ShiftBulkEditPayloadDto,
  UpdateShiftRequestDto,
} from './shift-service.dto';
import {
  GetShiftCreateData,
  getShiftCreateDataSchema,
} from './schema/get-shift-create-data.schema';
import {
  ShiftDetail,
  shiftDetailSchema,
  ShiftPopoverDetail,
  shiftPopoverDetailSchema,
} from './schema/shift-detail.schema';
import { ShiftDetailDto, ShiftPopoverDetailDto } from './dto/shift-detail.dto';

export class ShiftService {
  constructor(
    private readonly ogpApiService: OgpApiService,
    private readonly hqApiService: HqApiService
  ) {}

  public async getShiftCreateData(site: Site, companyId?: string): Promise<GetShiftCreateData> {
    const response = await this.getShiftCreateDataImpl(site, companyId);

    return getShiftCreateDataSchema.parse(response);
  }

  public async bulkUpdateShifts(
    site: Site,
    data: ShiftBulkEditPayloadDto
  ): Promise<BulkShiftActionData> {
    const response = await this.bulkUpdateShiftsImpl(site, data);

    return bulkShiftActionDataSchema.parse(response);
  }

  public async bulkArchiveShifts(
    site: Site,
    data: ShiftBulkArchivePayload
  ): Promise<BulkShiftActionData> {
    const response = await this.bulkArchiveShiftsImpl(site, data);

    return bulkShiftActionDataSchema.parse(response);
  }

  public async getBreakOptions(site: Site): Promise<{ breakOptions: BreakOption[] }> {
    const response = await this.getBreakOptionsImpl(site);
    return getBreakOptionsSchema.parse(response);
  }

  private async getBreakOptionsImpl(site: Site): Promise<{ breakOptions: BreakOption[] }> {
    if (site === 'hq') {
      return await this.hqApiService.get<{ breakOptions: BreakOption[] }>({
        path: 'break-options',
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.get<{ breakOptions: BreakOption[] }>({
        path: 'shifts/get-break-options',
      });
    }

    throw new Error('Not implemented');
  }

  private async bulkUpdateShiftsImpl(
    site: Site,
    data: ShiftBulkEditPayloadDto
  ): Promise<BulkShiftActionDataResponseDto> {
    const path = `shifts/bulk-update`;

    if (site === 'hq') {
      return await this.hqApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    throw new Error('Not implemented');
  }

  private async bulkArchiveShiftsImpl(
    site: Site,
    data: ShiftBulkArchivePayload
  ): Promise<BulkShiftActionDataResponseDto> {
    const path = `shifts/bulk-archive`;

    if (site === 'hq') {
      return await this.hqApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    throw new Error('Not implemented');
  }

  private async getShiftCreateDataImpl(
    site: Site,
    companyId: string | undefined
  ): Promise<GetShiftCreateDataResponseDto> {
    const path = `shifts/get-shift-create-data`;
    if (site === 'hq') {
      return await this.hqApiService.get<GetShiftCreateDataResponseDto>({
        path,
        params: { companyId },
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.get<GetShiftCreateDataResponseDto>({
        path,
      });
    }

    throw new Error('Not implemented');
  }

  public createShift = async (site: Site, shift: CreateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.create<string[]>({
        path: `shifts`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.create<string[]>({
        path: `shifts`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public createMultiDayShift = async (site: Site, shift: CreateMultiDayShiftRequestDto) => {
    if (site === 'ogp') {
      return await this.ogpApiService.create<string[]>({
        path: `shifts/multi-day`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public updateShift = async (site: Site, shift: UpdateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.update<void>({
        path: `shifts/${shift.id}`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.update<void>({
        path: `shifts/${shift.id}`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public duplicateShift = async (site: Site, shift: DuplicateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.create<string[]>({
        path: `shifts/duplicate`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.create<string[]>({
        path: `shifts/duplicate`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public getShiftDetail = async (site: Site, shiftId: string): Promise<ShiftDetail> => {
    const response = await this.getShiftDetailImpl(site, shiftId);
    return shiftDetailSchema.parse(response);
  };

  private async getShiftDetailImpl(site: Site, shiftId: string): Promise<ShiftDetailDto> {
    const path = `shifts/${shiftId}`;
    if (site === 'hq') {
      return await this.hqApiService.get<ShiftDetailDto>({
        path,
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.get<ShiftDetailDto>({
        path,
      });
    }
    throw new Error('Not implemented');
  }

  public getShiftPopoverDetail = async (
    site: Site,
    shiftId: string
  ): Promise<ShiftPopoverDetail> => {
    const response = await this.getShiftPopoverDetailImpl(site, shiftId);
    return shiftPopoverDetailSchema.parse(response);
  };

  private async getShiftPopoverDetailImpl(
    site: Site,
    shiftId: string
  ): Promise<ShiftPopoverDetailDto> {
    const path = `shifts/${shiftId}/popover-detail`;
    if (site === 'ogp') {
      return await this.ogpApiService.get<ShiftPopoverDetailDto>({
        path,
      });
    }
    throw new Error('Not implemented');
  }
}
